import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  merchantSalesCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type {
  MerchantSaleDto,
  SimulateWalletTransactionCompletedPayload,
} from "@/features/merchant/api/dto";

/**
 * Simule l'événement Kafka de finalisation d'une transaction wallet
 * (`POST /MerchantSales/simulate-wallet-transaction-completed`). RÉSERVÉ au
 * rôle `flashpay-service` (compte technique) — outil d'orchestration
 * back-office tant que Kafka n'est pas câblé (guide §6.6).
 */
export const useSimulateSaleCompleted = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["merchant", "sales", "simulate-completed"],
    mutationFn: (body: SimulateWalletTransactionCompletedPayload) =>
      mutateData({
        url: merchantEndpoints.merchantSales.simulateWalletTransactionCompleted,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<MerchantSaleDto>,
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, merchantSalesCacheKeys(), {
        resetListPage: true,
      });
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useSimulateSaleCompleted failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
};
